<!-- src/components/QuestionModal.vue -->
<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex items-center justify-center font-berthold-force"
        @keydown.esc.prevent="handleEsc"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/60"
          @click="canClose ? emit('close') : null"
        ></div>

        <!-- Wrapper para respeitar o spacing do flowbite -->
        <div class="relative p-4 w-full max-w-md max-h-full z-10">
          <!-- Dialog -->
          <div
            ref="panelRef"
            class="relative rounded-lg shadow-sm bg-black focus:outline-none border border-white"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
          >
            <!-- Header -->
            <div
              class="flex items-center justify-between uppercase p-4 py-2 md:p-5 md:py-2 border-b rounded-t-lg border-[#222222] bg-[#222222]"
            >
              <h3
                :id="titleId"
                class="text-lg font-semibold tracking-wider text-white"
              >
                {{ question?.title || "Pergunta" }}
              </h3>

              <!-- botão de fechar | desativado -->
              <button
                v-if="canClose"
                type="button"
                class="text-gray-400 bg-transparent rounded-lg text-sm h-8 w-8 ms-auto inline-flex justify-center items-center hover:bg-gray-600 hover:text-white"
                @click="emit('close')"
              >
                <svg
                  class="w-3 h-3"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span class="sr-only">Fechar</span>
              </button>
            </div>

            <!-- Body -->
            <div class="p-3 py-2 md:p-5 md:py-2">
              <p class="mb-4 tracking-wider text-white">
                {{ question?.prompt }}
              </p>

              <!-- Opções -->
              <ul class="space-y-4 mb-4">
                <li v-for="opt in safeOptions" :key="opt.id">
                  <input
                    :id="`opt-${opt.id}`"
                    type="radio"
                    class="hidden peer"
                    name="question-option"
                    :value="opt.id"
                    v-model="selectedId"
                  />
                  <label
                    :for="`opt-${opt.id}`"
                    class="inline-flex items-center justify-between w-full p-3 py-2 md:p-5 md:py-2 rounded-lg cursor-pointer tracking-wider text-white bg-[#222222] border-white hover:border-[#d20a0a] peer-checked:text-[#d20a0a] peer-checked:bg-white peer-checked:border-[#d20a0a] border-2 hover:bg-white hover:text-red-700 transition-colors duration-300
                    "
                  >
                    <div class="block">
                      <div
                        class="w-full text-base md:text-lg font-semibold tracking-wider"
                      >
                        {{ opt.text }}
                      </div>
                    </div>

                    <svg
                      class="w-4 h-4 ms-3 rtl:rotate-180"
                      viewBox="0 0 14 10"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M1 5h12m0 0L9 1m4 4L9 9"
                      />
                    </svg>
                  </label>
                </li>
              </ul>

              <!-- Rodapé -->
              <div class="flex items-center justify-between tracking-wider">
                <span class="text-xs text-gray-400">
                  Tempo: {{ question?.time_label || "--:--" }}
                </span>

                <button
                  type="button"
                  class="inline-flex items-center justify-center px-5 py-2.5 text-base focus:outline-none uppercase text-white font-bold tracking-wide bg-[#d20a0a] border-2 border-red-700 hover:bg-white hover:text-red-700 transition-colors duration-300 disabled:hover:text-gray-300 disabled:text-gray-400 disabled:bg-[#222222] disabled:border-2 disabled:border-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  :disabled="question?.time_label && !selectedId"
                  :title="question?.time_label && !selectedId ? 'Selecione uma opção para prosseguir' : 'Continuar aula'"
                  @click="submit"
                >
                  Continuar
                  <svg
                    class="w-4 h-4 ms-1.5 -me-0.5"
                    viewBox="0 0 14 10"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M1 5h12m0 0L9 1m4 4L9 9"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";

const props = defineProps({
  open: { type: Boolean, default: false },
  question: { type: Object, default: null }, // { id, title, prompt, time_seconds, time_label, required, options: [...] }
  canClose: { type: Boolean, default: false }, // se quiser permitir fechar sem responder
});

const emit = defineEmits(["close", "  "]);

const selectedId = ref(null);
const panelRef = ref(null);
const titleId = `question-title-${Math.random().toString(36).slice(2, 8)}`;

const safeOptions = computed(() => {
  const arr = Array.isArray(props.question?.options)
    ? props.question.options
    : [];
  // opcional: ordenar por "order"
  return [...arr].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
});

watch(
  () => props.question,
  () => {
    selectedId.value = null; // reset ao trocar de pergunta
  },
  { immediate: true }
);

function handleEsc() {
  if (props.canClose) emit("close");
}

function submit() {
  emit("submit", {
    questionId: props.question?.id,
    optionId: selectedId.value,
    points: props.question?.points,
  });
  // Não fecha automaticamente: deixe o pai decidir (validar, salvar, etc.)
}

// foco inicial acessível
function focusPanel() {
  requestAnimationFrame(() => panelRef.value?.focus?.());
}
watch(
  () => props.open,
  (v) => v && focusPanel()
);
onMounted(() => props.open && focusPanel());
onBeforeUnmount(() => {
  /* nada por enquanto */
});
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
